// src/components/SharePointSearch/sharepointSearchUtils.ts
// Formatting utilities for SharePointSearch components

const MIME_TO_EXTENSION: Record<string, string> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
  "application/pdf": "pdf",
};

/** Partial MIME substrings to extension — fallback when exact match fails */
const MIME_KEYWORD_TO_EXTENSION: Array<[string, string]> = [
  ["word", "docx"],
  ["docx", "docx"],
  ["pdf", "pdf"],
];

const MIME_TO_LABEL: Record<string, string> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Document",
  "application/msword": "Word Document",
  "application/pdf": "PDF Document",
};

/** Format byte count to human-readable size (e.g., 245760 -> "240 KB") */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Format ISO date to short readable format (e.g., "Mar 1, 2026") */
export function formatDate(isoDate: string): string {
  if (!isoDate) return "";
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return isoDate;
  }
}

/**
 * Get file extension from MIME type, with fallback to filename parsing.
 * Tries exact MIME match first, then keyword matching, then filename extension.
 */
export function getFileExtension(mimeType: string, fileName?: string): string {
  // 1. Exact MIME match
  if (MIME_TO_EXTENSION[mimeType]) return MIME_TO_EXTENSION[mimeType];

  // 2. MIME keyword fallback (handles non-standard MIME strings)
  const lowerMime = mimeType.toLowerCase();
  for (const [keyword, ext] of MIME_KEYWORD_TO_EXTENSION) {
    if (lowerMime.includes(keyword)) return ext;
  }

  // 3. Filename extension fallback
  if (fileName) {
    const match = fileName.match(/\.(\w+)$/);
    if (match) {
      const fileExt = match[1].toLowerCase();
      if (fileExt === "pdf") return "pdf";
      if (fileExt === "docx" || fileExt === "doc") return "docx";
    }
  }

  return "file";
}

/** Get human-readable file type label from MIME type */
export function getFileTypeLabel(mimeType: string): string {
  return MIME_TO_LABEL[mimeType] || "Document";
}
